/*
  Ethiraj — the buy bar that follows the shopper up the page.

  Two jobs, and deliberately no more than two:

    1. Show the bar once the real Add to cart button has scrolled off the
       top of the screen, and hide it again when it comes back.
    2. Keep the bar's label, disabled state and price the same as the real
       control's.

  It does not add anything to the cart itself. Its button carries
  `form="product-form-<section>"`, so pressing it fires a submit on Dawn's
  own product form and Dawn's own product-form.js handles it — which is
  why the variant picker and the quantity stepper need no wiring here at
  all: they are read out of the real form at submit time, as always.

  Everything the bar mirrors is read from the live DOM rather than
  recomputed. Dawn re-renders the price and re-labels the button on every
  variant change, so copying its answer is both shorter and impossible to
  get out of step with.
*/

if (!customElements.get('ethiraj-sticky-buy')) {
  customElements.define(
    'ethiraj-sticky-buy',
    class EthirajStickyBuy extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('[data-ehf-sticky-submit]');
        this.buttonLabel = this.querySelector('[data-ehf-sticky-label]');
        this.priceEl = this.querySelector('[data-ehf-sticky-price]');
        this.wasEl = this.querySelector('[data-ehf-sticky-was]');

        /*
          The real controls. If either is missing the product is not for
          sale through a form on this page — a gift card preview, or a
          section rendered without buy buttons — and a bar offering to add
          it to the cart would be a lie. Leave it parked.
        */
        this.realButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.realPrice = document.getElementById(`price-${this.sectionId}`);
        if (!this.realButton || !this.button) return;

        this.sync();
        this.watch();

        /*
          Dawn publishes this after it has already swapped the new price
          into the page and re-labelled the button, so by the time this
          runs there is nothing left to wait for.
        */
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.sectionId) return;
            this.sync();
          });
        }
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.observer?.disconnect();
      }

      /* ── Mirroring ──────────────────────────────────────────────── */

      sync() {
        this.syncButton();
        this.syncPrice();
      }

      syncButton() {
        const disabled =
          this.realButton.hasAttribute('disabled') ||
          this.realButton.getAttribute('aria-disabled') === 'true';

        this.button.toggleAttribute('disabled', disabled);

        /*
          The real button's own label already says the right thing in the
          shop's language — Add to cart, Sold out, Unavailable — so it is
          copied rather than chosen again here. Its span holds the words;
          the loading spinner beside it is not wanted.
        */
        const label = this.realButton.querySelector('span:not(.loading__spinner):not(.svg-wrapper)');
        if (label && this.buttonLabel) {
          this.buttonLabel.textContent = label.textContent.trim();
        }
      }

      syncPrice() {
        if (!this.realPrice || !this.priceEl) return;

        const onSale = this.realPrice.querySelector('.price')?.classList.contains('price--on-sale');
        const now = onSale
          ? this.realPrice.querySelector('.price__sale .price-item--sale')
          : this.realPrice.querySelector('.price__regular .price-item--regular');

        if (now) this.priceEl.textContent = now.textContent.trim();

        if (this.wasEl) {
          const was = onSale ? this.realPrice.querySelector('.price__sale s.price-item--regular') : null;
          this.wasEl.textContent = was ? was.textContent.trim() : '';
          this.wasEl.hidden = !was;
        }
      }

      /* ── Showing and hiding ─────────────────────────────────────── */

      watch() {
        /*
          Out of view is not enough on its own: at the top of a phone page
          the buy button is out of view because it is still below the fold,
          and sliding a bar up over a shopper who has not started reading
          is the behaviour that gives these bars a bad name. The bar
          appears only once the button has gone off the TOP — which is
          what `top < 0` distinguishes.
        */
        this.observer = new IntersectionObserver(
          ([entry]) => {
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', scrolledPast);
          },
          { threshold: 0 }
        );

        this.observer.observe(this.realButton);
      }
    }
  );
}
